import { h, Component } from 'preact';
import ChatFrame from './chat-frame';
import ChatFloatingButton from './chat-floating-button';
import ChatTitleMsg from './chat-title-msg';
import ArrowIcon from './arrow-icon';
import {
    desktopTitleStyle,
    desktopWrapperStyle,
    mobileOpenWrapperBase,
    mobileClosedWrapperStyle,
    desktopClosedWrapperStyleChat
} from './style';

export default class Widget extends Component {

    constructor(props) {
        super(props);
        // matchMedia query — fires on breakpoint cross, not on every resize pixel
        this.mobileQuery = window.matchMedia('(max-width: 667px)');
        this.state = {
            isChatOpen: false,
            pristine: true,
            wasChatOpened: this.wasChatOpened(),
            // Real viewport height — fixes iOS 100vh bug
            viewportHeight: this.getViewportHeight(),
            // Reactive isMobile — updates on orientation change / resize
            isMobile: this.mobileQuery.matches
        };
    }

    getViewportHeight() {
        // visualViewport is the most accurate (excludes keyboard on iOS)
        if (window.visualViewport) return window.visualViewport.height;
        return window.innerHeight;
    }

    componentDidMount() {
        // Update height on resize (orientation change, keyboard open/close)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.onViewportResize);
        } else {
            window.addEventListener('resize', this.onViewportResize);
        }

        // React to breakpoint changes (rotation, resize crossing 667px)
        // addEventListener is preferred; addListener is legacy fallback for old Safari
        if (this.mobileQuery.addEventListener) {
            this.mobileQuery.addEventListener('change', this.onMobileQueryChange);
        } else {
            this.mobileQuery.addListener(this.onMobileQueryChange);
        }
    }

    componentWillUnmount() {
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.onViewportResize);
        } else {
            window.removeEventListener('resize', this.onViewportResize);
        }

        if (this.mobileQuery.removeEventListener) {
            this.mobileQuery.removeEventListener('change', this.onMobileQueryChange);
        } else {
            this.mobileQuery.removeListener(this.onMobileQueryChange);
        }
    }

    onViewportResize = () => {
        this.setState({ viewportHeight: this.getViewportHeight() });
    };

    onMobileQueryChange = (e) => {
        this.setState({ isMobile: e.matches });
    };

    render({ conf }, { isChatOpen, pristine, viewportHeight, isMobile }) {
        const desktopHeight = (viewportHeight - 100 < conf.desktopHeight)
            ? viewportHeight - 90
            : conf.desktopHeight;

        const headerBg    = conf.headerColor || '#1a1a2e';
        const accentColor = conf.mainColor;

        // ── Wrapper style ─────────────────────────────────────────────────────
        let wrapperStyle;
        if (!isChatOpen && (isMobile || conf.alwaysUseFloatingButton)) {
            wrapperStyle = { ...mobileClosedWrapperStyle };
        } else if (!isMobile) {
            wrapperStyle = (conf.closedStyle === 'chat' || isChatOpen || this.wasChatOpened())
                ? (isChatOpen
                    ? { ...desktopWrapperStyle, width: conf.desktopWidth + 'px', height: desktopHeight + 'px' }
                    : { ...desktopWrapperStyle })
                : { ...desktopClosedWrapperStyleChat };
        } else {
            // Mobile open: use real viewport height to avoid iOS 100vh bug
            wrapperStyle = {
                ...mobileOpenWrapperBase,
                height: viewportHeight + 'px'
            };
        }

        // ── Header height: add safe-area-inset-top on mobile ─────────────────
        // We read the CSS env value via a trick: use inline style calc()
        const headerStyle = {
            ...desktopTitleStyle,
            background: headerBg,
            // On mobile add extra top padding for notch / Dynamic Island
            paddingTop: isMobile ? 'max(12px, env(safe-area-inset-top, 12px))' : '0',
            // Header is taller on mobile to account for safe area
            minHeight: isMobile ? '64px' : '68px',
            paddingBottom: '12px'
        };

        // ── Iframe container height ───────────────────────────────────────────
        const iframeContainerStyle = {
            display: isChatOpen ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,      // critical for flex overflow
            overflow: 'hidden'
        };
        // On desktop (non-flex wrapper), set explicit height
        if (!isMobile && isChatOpen) {
            iframeContainerStyle.height = desktopHeight + 'px';
        }

        return (
            <div style={wrapperStyle}>

                {/* ── Floating button: mobile closed ── */}
                {(isMobile || conf.alwaysUseFloatingButton) && !isChatOpen
                    ? <ChatFloatingButton color={accentColor} onClick={this.onClick} />

                    : (conf.closedStyle === 'chat' || isChatOpen || this.wasChatOpened())
                        ? (
                            /* ── Header bar ── */
                            <div style={headerStyle} onClick={this.onClick}>
                                {/* Left: avatar + operator info */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                        <div style={{
                                            width: '40px', height: '40px',
                                            borderRadius: '50%',
                                            background: accentColor,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0
                                        }}>
                                            {conf.avatarUrl
                                                ? <img src={conf.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                                    <path d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#0d0d0d" />
                                                  </svg>
                                            }
                                        </div>
                                        {/* Online dot */}
                                        <div style={{
                                            position: 'absolute', bottom: '1px', right: '1px',
                                            width: '10px', height: '10px',
                                            borderRadius: '50%',
                                            background: '#4ade80',
                                            border: `2px solid ${headerBg}`
                                        }} />
                                    </div>

                                    <div style={{ minWidth: 0 }}>
                                        <div style={{
                                            color: '#fff', fontWeight: '600', fontSize: '15px',
                                            lineHeight: '1.2',
                                            fontFamily: "'Inter', sans-serif",
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                        }}>
                                            {isChatOpen ? conf.titleOpen : conf.titleClosed}
                                        </div>
                                        <div style={{
                                            color: 'rgba(255,255,255,0.6)', fontSize: '12px',
                                            lineHeight: '1.3', fontFamily: "'Inter', sans-serif"
                                        }}>
                                            {conf.operatorStatus || 'Онлайн · Ответим быстро'}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: close/chevron button — 44px touch target on mobile */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: '44px', height: '44px', flexShrink: 0,
                                    color: 'rgba(255,255,255,0.8)'
                                }}>
                                    {isMobile && isChatOpen
                                        ? /* X close button on mobile */
                                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                              <path d="M1 1l16 16M17 1L1 17" stroke="white" stroke-width="2" stroke-linecap="round"/>
                                          </svg>
                                        : <ArrowIcon isOpened={isChatOpen} />
                                    }
                                </div>
                            </div>
                        )
                        : <ChatTitleMsg onClick={this.onClick} conf={conf} />
                }

                {/* ── Chat iframe container ── */}
                <div style={iframeContainerStyle}>
                    {pristine ? null : <ChatFrame {...this.props} isMobile={isMobile} />}
                </div>

            </div>
        );
    }

    onClick = () => {
        const stateData = {
            pristine: false,
            isChatOpen: !this.state.isChatOpen
        };
        if (!this.state.isChatOpen && !this.wasChatOpened()) {
            this.setCookie();
            stateData.wasChatOpened = true;
        }
        this.setState(stateData);
    };

    setCookie = () => {
        const d = new Date();
        d.setTime(d.getTime() + (parseInt(this.props.conf.cookieExpiration) * 86400000));
        document.cookie = `chatwasopened=1; expires=${d.toGMTString()}; path=/`;
    };

    getCookie = () => {
        const eq = 'chatwasopened=';
        for (let c of document.cookie.split(';')) {
            c = c.trim();
            if (c.startsWith(eq)) return c.substring(eq.length);
        }
        return false;
    };

    wasChatOpened = () => this.getCookie() !== false;
}



// WEBPACK FOOTER //
// ./src/widget/widget.js